// Minimal DPoP helpers using Web Crypto (ES256)

function base64url(input: ArrayBuffer | string): string {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : new Uint8Array(input);
  let str = '';
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function hexToBytes(hex: string): Uint8Array {
  const clean = hex.replace(/^0x/, '');
  const arr = new Uint8Array(clean.length / 2);
  for (let i = 0; i < arr.length; i++) arr[i] = parseInt(clean.substr(i * 2, 2), 16);
  return arr;
}

// Convert DER ECDSA signature to JOSE (r||s raw, base64url)
function derToJoseSig(der: ArrayBuffer): string {
  const bytes = new Uint8Array(der);
  // Very small DER parser for ECDSA-Sig-Value ::= SEQUENCE { r INTEGER, s INTEGER }
  // Assumes both r and s are positive and up to 32 bytes (P-256)
  let offset = 0;
  if (bytes[offset++] !== 0x30) throw new Error('Invalid DER');
  const seqLen = bytes[offset++];
  if (seqLen + 2 !== bytes.length) { /* best-effort */ }

  if (bytes[offset++] !== 0x02) throw new Error('Invalid DER int r');
  let rLen = bytes[offset++];
  let r = bytes.slice(offset, offset + rLen);
  offset += rLen;

  if (bytes[offset++] !== 0x02) throw new Error('Invalid DER int s');
  let sLen = bytes[offset++];
  let s = bytes.slice(offset, offset + sLen);

  // Remove possible leading zeros and pad to 32 bytes
  while (r.length > 0 && r[0] === 0) r = r.slice(1);
  while (s.length > 0 && s[0] === 0) s = s.slice(1);
  if (r.length > 32 || s.length > 32) throw new Error('Invalid r/s length');
  const rPadded = new Uint8Array(32); rPadded.set(r, 32 - r.length);
  const sPadded = new Uint8Array(32); sPadded.set(s, 32 - s.length);
  const raw = new Uint8Array(64);
  raw.set(rPadded, 0); raw.set(sPadded, 32);
  return base64url(raw.buffer);
}

function uuidv4(): string {
  // RFC4122 v4 using crypto.getRandomValues
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  const hex = Array.from(bytes).map(toHex).join('');
  return `${hex.substr(0, 8)}-${hex.substr(8, 4)}-${hex.substr(12, 4)}-${hex.substr(16, 4)}-${hex.substr(20)}`;
}

export async function createDPoPProof(
  privateKey: CryptoKey,
  publicJwk: JsonWebKey,
  method: string,
  url: string
): Promise<string> {
  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: publicJwk,
  } as const;
  const payload = {
    htm: method.toUpperCase(),
    htu: url,
    iat: Math.floor(Date.now() / 1000),
    jti: uuidv4(),
  };
  const encodedHeader = base64url(JSON.stringify(header));
  const encodedPayload = base64url(JSON.stringify(payload));
  const signingInput = `${encodedHeader}.${encodedPayload}`;

  const signatureDer = await crypto.subtle.sign(
    { name: 'ECDSA', hash: { name: 'SHA-256' } },
    privateKey,
    new TextEncoder().encode(signingInput)
  );
  const signatureJose = derToJoseSig(signatureDer);
  return `${signingInput}.${signatureJose}`;
}

export async function generateEphemeralKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    true,
    ['sign', 'verify']
  ) as Promise<CryptoKeyPair>;
}

export async function exportPublicJWK(publicKey: CryptoKey): Promise<Record<string, unknown>> {
  const jwk = await crypto.subtle.exportKey('jwk', publicKey);
  return jwk as Record<string, unknown>;
}


